import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { functionsService } from '@/services/functionsService';
import { Navigate } from 'react-router-dom';
import { TranscriptManagement } from './admin/TranscriptManagement';
import { Analytics } from './admin/Analytics';
import { PromptManagement } from './admin/PromptManagement';
import { ApiKeyManagement } from './admin/ApiKeyManagement';
import { AboutManagement } from './admin/AboutManagement';

interface User {
  id: string;
  email: string;
  displayName?: string;
  tier: 'free' | 'premium' | 'enterprise';
  balance: number;
}

export const Admin: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'transcripts' | 'filings' | 'credits' | 'analytics' | 'prompts' | 'apikeys' | 'about'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) {
      setCheckingAdmin(false);
      return;
    }

    try {
      // Force refresh: claims like isAdmin update server-side; cached ID token may omit them
      const idTokenResult = await user.getIdTokenResult(true);
      const adminStatus = idTokenResult.claims.isAdmin === true;
      setIsAdmin(adminStatus);
      
      if (adminStatus) {
        loadUsers();
      }
    } catch (error) {
      console.error('Failed to check admin status:', error);
    } finally {
      setCheckingAdmin(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const result = await functionsService.listUsersForAdmin();
      if (!result.success || !result.users) {
        setUsers([]);
        return;
      }
      const usersList: User[] = result.users.map((u) => ({
        id: u.uid,
        email: u.email,
        displayName: u.displayName || undefined,
        tier: (u.tier as 'free' | 'premium' | 'enterprise') || 'free',
        balance: u.balance ?? 0,
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserTier = async (userId: string, newTier: 'free' | 'premium' | 'enterprise') => {
    try {
      await functionsService.adminUpdateUserTier(userId, newTier);
      await loadUsers();
    } catch (error) {
      console.error('Failed to update user tier:', error);
      alert('Failed to update user tier');
    }
  };

  const handleAddCredits = async (userId: string, amount: number) => {
    try {
      await functionsService.adminAddCredits(userId, amount);
      await loadUsers();
    } catch (error) {
      console.error('Failed to add credits:', error);
      alert('Failed to add credits');
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const filteredUsers = users.filter(
    (u) =>
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Admin Panel</h1>
          <p className="body-text text-gray-600">
            Manage users, content, credits, and system settings
          </p>
        </div>

        <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
          {['users', 'transcripts', 'filings', 'credits', 'analytics', 'prompts', 'apikeys', 'about'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-blazerex-green text-blazerex-green font-header-bold'
                  : 'border-transparent text-gray-600 hover:text-blazerex-green'
              }`}
            >
              {tab === 'apikeys' ? 'API Keys' : tab === 'about' ? 'About Page' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'users' && (
          <div>
            <div className="mb-6">
              <Input
                type="text"
                placeholder="Search users by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <Card>
                <div className="text-center py-12">
                  <p className="body-text text-gray-600">No users found.</p>
                  <p className="body-text text-sm text-gray-500 mt-1">
                    {searchTerm ? 'Try a different search.' : 'User list is loaded from Firebase Auth.'}
                  </p>
                </div>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 sub-header">Email</th>
                        <th className="text-left py-3 px-4 sub-header">Name</th>
                        <th className="text-left py-3 px-4 sub-header">Tier</th>
                        <th className="text-left py-3 px-4 sub-header">Credits</th>
                        <th className="text-left py-3 px-4 sub-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 body-text">{u.email}</td>
                          <td className="py-3 px-4 body-text">{u.displayName || '—'}</td>
                          <td className="py-3 px-4">
                            <select
                              value={u.tier}
                              onChange={(e) => handleUpdateUserTier(u.id, e.target.value as any)}
                              className="body-text border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="free">Free</option>
                              <option value="premium">Premium</option>
                              <option value="enterprise">Enterprise</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 body-text font-mono">{u.balance}</td>
                          <td className="py-3 px-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const amount = prompt('Enter credit amount to add:');
                                if (amount != null && amount !== '') {
                                  const n = parseInt(amount, 10);
                                  if (!Number.isNaN(n) && n > 0) {
                                    handleAddCredits(u.id, n);
                                  } else {
                                    alert('Please enter a positive number.');
                                  }
                                }
                              }}
                            >
                              Add Credits
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'transcripts' && <TranscriptManagement />}

        {activeTab === 'filings' && (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600 mb-4">Filing management coming soon</p>
              <p className="body-text text-sm text-gray-500">
                Upload, edit, and manage 10-K/10-Q filings
              </p>
            </div>
          </Card>
        )}

        {activeTab === 'credits' && (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600 mb-4">Credit management</p>
              <p className="body-text text-sm text-gray-500">
                Configure credit costs and daily allowances
              </p>
            </div>
          </Card>
        )}

        {activeTab === 'analytics' && <Analytics />}

        {activeTab === 'prompts' && <PromptManagement />}

        {activeTab === 'apikeys' && <ApiKeyManagement />}

        {activeTab === 'about' && <AboutManagement />}
      </div>
    </div>
  );
};

